/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import filter from 'lodash/filter';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import set from 'lodash/set';
import sortBy from 'lodash/sortBy';
import { localToday, utcStringToCalendarDate } from 'sarsaparilla';

import type { Dispatch } from 'redux';
import { internalAuthCampingAxios } from 'ui-internal-camping/src/utils/internalCampingAxios';

import * as types from '../constants';
import { campingAxios } from '../utils/campingAxios';
import { campingSearchAxios } from '../utils/externalAxios';
import * as utils from '../utils/utils';
import { APIType } from './types';

export const fetchCampgroundWorker =
    (campground: { id: string }, apiType: APIType) => (dispatch: Dispatch<any>) => {
        const localAxios =
            apiType === APIType.Internal ? internalAuthCampingAxios : campingAxios;

        return localAxios(`/campgrounds/${campground.id}`)
            .then(utils.status)
            .then((json) => json.data)
            .then((json) => {
                const output = utils.transformCampgroundFromBackEnd(json.campground);
                if (output.facility_id === '') {
                    dispatch(
                        utils.requestFailure(
                            types.GET_CAMPGROUND_ERROR,
                            'Error fetching campground.'
                        )
                    );
                } else {
                    dispatch(utils.requestSuccess(types.GET_CAMPGROUND, output));
                }
                return Promise.resolve(output);
            })
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch(
                        utils.requestFailure(
                            types.GET_CAMPGROUND_ERROR,
                            'Campground unavailable at this time.'
                        )
                    );
                }
            });
    };

export const fetchCampground =
    (campground: { id: string }) => (dispatch: Dispatch<any>) => {
        return fetchCampgroundWorker(campground, APIType.External)(dispatch);
    };

export const fetchCampgroundRatesWorker =
    (campground: { id: string }, apiType: APIType) => (dispatch: Dispatch<any>) => {
        const localAxios =
            apiType === APIType.Internal ? internalAuthCampingAxios : campingAxios;

        return localAxios(`/campgrounds/${campground.id}/rates`)
            .then(utils.status)
            .then((json) => json.data)
            .then((json) => {
                const filteredSeasons = get(json, 'rates_list', []).filter(
                    (season: { season_end: string }) =>
                        utcStringToCalendarDate(season.season_end)!.compare(localToday) >=
                        0
                );
                const sortedSeasons = sortBy(filteredSeasons, ['season_start']);
                dispatch(utils.requestSuccess(types.GET_CAMPGROUND_RATES, sortedSeasons));
                set(json, 'rates_list', sortedSeasons);

                return json;
            })
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch(
                        utils.requestFailure(
                            types.GET_CAMPGROUND_RATES_ERROR,
                            'No campground rates at this time.'
                        )
                    );
                }
            });
    };

export const fetchCampgroundRates =
    (campground: { id: string }) => (dispatch: Dispatch<any>) => {
        return fetchCampgroundRatesWorker(campground, APIType.External)(dispatch);
    };

export const fetchSiteSchedulesWorker =
    (facilityId: string, apiType: APIType) => (dispatch: Dispatch<any>) => {
        const localAxios =
            apiType === APIType.Internal ? internalAuthCampingAxios : campingAxios;

        return localAxios
            .get(`/campgrounds/${facilityId}/schedules`)
            .then(utils.status)
            .then((json) => json.data)
            .then((json) => {
                dispatch(utils.requestSuccess(types.GET_CAMPGROUND_SCHEDULES, json));
            })
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch(
                        utils.requestFailure(
                            types.GET_CAMPGROUND_SCHEDULES_ERROR,
                            'No campground schedules at this time.'
                        )
                    );
                }
            });
    };

export const fetchSiteSchedules = (facilityId: string) => (dispatch: Dispatch<any>) => {
    return fetchSiteSchedulesWorker(facilityId, APIType.External)(dispatch);
};

export const fetchCampgroundGalleryWorker =
    (facilityId: string, apiType: APIType) => (dispatch: Dispatch<any>) => {
        const localAxios =
            apiType === APIType.Internal ? internalAuthCampingAxios : campingAxios;
        localAxios
            .get(`/api/media/public/asset/${facilityId}`, {})
            .then((res) => {
                if (res && !res.data.err) {
                    // if campground contains images; else no images are available, images state remain empty
                    if (res.data.result) {
                        const images = filter(res.data.result, {
                            is_gallery: true,
                        });
                        const mediaItems = images.map((item) => {
                            return {
                                altText: item.title || item.description,
                                caption: item.description,
                                url: item.url,
                                credit: item.credits,
                            };
                        });

                        dispatch(
                            utils.requestSuccess(types.GET_CAMPGROUND_GALLERY, mediaItems)
                        );
                    }
                }
            })
            .catch((error) => {
                throw error;
            });
    };

export const fetchCampgroundGallery =
    (facilityId: string) => (dispatch: Dispatch<any>) => {
        return fetchCampgroundGalleryWorker(facilityId, APIType.External)(dispatch);
    };

export const fetchFacilitySitesWorker =
    (facilityId: string, apiType: APIType) => (dispatch: Dispatch<any>) => {
        const localAxios =
            apiType === APIType.Internal ? internalAuthCampingAxios : campingAxios;

        return localAxios(`/campgrounds/${facilityId}/campsites`)
            .then(utils.status)
            .then((json) => json.data)
            .then((json) => {
                dispatch(utils.requestSuccess(types.GET_CAMPSITE_LIST, json.campsites));
                return json.campsites;
            })
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch(
                        utils.requestFailure(
                            types.GET_CAMPSITE_LIST_ERROR,
                            'No facility sites at this time.'
                        )
                    );
                }
            });
    };

export const fetchFacilitySites = (facilityId: string) => (dispatch: Dispatch<any>) => {
    return fetchFacilitySitesWorker(facilityId, APIType.External)(dispatch);
};

export const fetchCampgroundRecreationSites =
    (facilityId: string) => (dispatch: Dispatch<any>) => {
        const args = {
            fq: `asset_id:${facilityId}`,
            agg: ['campsite_type_of_use', 'campsite_equipment_name', 'campsite_type'],
            size: '0',
        };
        const url = utils.urlWithParams(`${process.env.API}/search/campsites`, args);
        return campingSearchAxios
            .get(url)
            .then(utils.status)
            .then((json) => json.data)
            .then((json) => {
                let output;
                if (json.aggregations) {
                    dispatch(
                        utils.requestSuccess(
                            types.GET_CAMPGROUND_RECREATION_SITES,
                            json.aggregations
                        )
                    );
                    output = json;
                }
                return Promise.resolve(output);
            })
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch(
                        utils.requestFailure(
                            types.GET_CAMPGROUND_RECREATION_SITES_ERROR,
                            'No campground recreation sites at this time.'
                        )
                    );
                }
                return Promise.resolve(error);
            });
    };

export const fetchTotalCampsites = (facilityId: string) => {
    const args = {
        fq: `asset_id:${facilityId}`,
        size: '0',
    };
    const url = utils.urlWithParams(`${process.env.API}/search/campsites`, args);
    return campingSearchAxios
        .get(url)
        .then(utils.status)
        .catch(utils.catchError)
        .then((json) => {
            let total = 0;
            const output = json.data;
            if ('total' in output) {
                total = output.total;
            }
            return Promise.resolve(total);
        });
};

export const fetchCampgroundPills =
    (campgroundId: string) => (dispatch: Dispatch<any>) => {
        const args = {
            fq: [`id:${campgroundId}_asset`],
        };
        const url = utils.urlWithParams(`${process.env.API}/search`, args);
        return campingSearchAxios
            .get(url)
            .then(utils.status)
            .catch(utils.catchError)
            .then((json) => json.data)
            .then((json) => {
                const payload = json;
                let output;
                if (!payload.err) {
                    if (payload.size > 0) {
                        output = payload.results[0].campsite_type_of_use || [];
                        dispatch(
                            utils.requestSuccess(types.GET_CAMPGROUND_TYPE_OF_USE, output)
                        );
                    }
                }
                return Promise.resolve(output);
            });
    };

export const fetchReleaseDatesWorker =
    (campgroundId: string, apiType: APIType) => (dispatch: Dispatch<any>) => {
        const localAxios =
            apiType === APIType.Internal ? internalAuthCampingAxios : campingAxios;

        return localAxios
            .get(`/campgrounds/${campgroundId}/releases`)
            .then(utils.status)
            .then((json) => json.data)
            .then((json) => {
                const payload = json;

                dispatch({
                    type: types.GET_RELEASE_DATES,
                    payload,
                });
                return Promise.resolve(payload);
            })
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch(
                        utils.requestFailure(
                            types.GET_RELEASE_DATES_ERROR,
                            error.response.data
                        )
                    );
                }
            });
    };

export const fetchReleaseDates = (campgroundId: string) => {
    return fetchReleaseDatesWorker(campgroundId, APIType.External);
};

export const fetchCampgroundSiteTypesCount =
    (campgroundId: string) => (dispatch: Dispatch<any>) => {
        const args = {
            fq: `asset_id:${campgroundId}`,
            agg: ['type'],
            size: '0',
        };

        const url = utils.urlWithParams(`${process.env.API}/search/campsites`, args);

        return campingSearchAxios
            .get(url)
            .then(utils.status)
            .then((json) => json.data)
            .then((json) => {
                let output;
                if (json.aggregations) {
                    dispatch(
                        utils.requestSuccess(
                            types.GET_CAMPGROUND_SITE_TYPES_COUNT,
                            json.aggregations
                        )
                    );
                    output = json;
                }
                return Promise.resolve(output);
            })
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch(
                        utils.requestFailure(
                            types.GET_CAMPGROUND_SITE_TYPES_COUNT_ERROR,
                            error.response.data
                        )
                    );
                }
                return Promise.resolve(error);
            });
    };

export const fetchCampgroundSearchResult =
    (campgroundId: string) => (dispatch: Dispatch<any>) => {
        return campingSearchAxios(
            `${process.env.API}/search?fq=entity_type:campground&fq=entity_id:${campgroundId}`
        )
            .then(utils.status)
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch(
                        utils.requestFailure(
                            types.GET_CAMPGROUND_SITE_TYPES_COUNT_ERROR,
                            error.response.data
                        )
                    );
                }
                return Promise.resolve(error);
            })
            .then((json) => json.data)
            .then((json) => {
                let payload = {};

                if (json && !isEmpty(json.results)) {
                    payload = json.results[0];
                }

                dispatch(
                    utils.requestSuccess(types.GET_CAMPGROUND_SEARCH_RESULT, payload)
                );
            });
    };
